// mu — typed-error → exit-code mapping + CLI handler wrapper.
//
// classifyError + emitError + handle live here so cli.ts is just argument parsing /
// workstream resolution / program wiring; the typed-error catalogue
// belongs next to its own helper. cli.ts re-exports `classifyError`
// and `handle` for back-compat with existing tests + cli/* importers.
//
// Exit codes (docs/guide/sql.md § Script with --json):
//   0 = success
//   1 = generic error
//   2 = usage error (validation / missing-flag / type / mutex /
//       unknown subcommand) — commander-detected mistakes AND
//       handler-thrown UsageError. Both surfaces print the failing
//       subcommand's --help (human path) or include a structured
//       `usage` field (--json path).
//   3 = not found (no such agent / task / pane)
//   4 = conflict (name collision, double-claim, cycle, etc.)
//   5 = substrate unavailable or unsafe (no multiplexer, DB locked,
//       ops-log drift, a `mu doctor` FAIL row, an abort/interrupt that
//       did not settle in time; a wait timeout is NOT here: it is exit 0)
//   6 / 7 = `mu wait` results, not errors: an owner died / needs attention
//       (`WaitResult.exit`, src/wait.ts); no error class maps here.

import { type Command, CommanderError } from "commander";
import {
  CarrierJobKeptError,
  CarrierJobUnresolvedError,
  CarrierJobUnstoppedError,
} from "../agents/lease-carrier.js";
import {
  AgentAbortNeedsCtlError,
  AgentAbortTimeoutError,
  AgentBusyError,
  AgentCtlUnreachableError,
  AgentDiedOnSpawnError,
  AgentEmptySendError,
  AgentExistsError,
  AgentExtensionOutdatedError,
  AgentFreshNeedsCtlError,
  AgentLockBusyError,
  AgentNameExhaustedError,
  AgentNotFoundError,
  AgentNotInWorkstreamError,
  AgentPaneStillAliveError,
  AgentReplacedError,
  AgentRestartedError,
  AgentSendIndeterminateError,
  AgentSendUnsupportedError,
  AgentSlashCommandUnsupportedError,
  AgentSpawnCliNotFoundError,
  AgentSpawnStartupError,
  SpawnAttemptBusyError,
  SpawnAttemptEndedError,
  SpawnAttemptFailedError,
  SpawnAttemptGuardError,
  SpawnReceiptConflictError,
  SpawnTaskStateError,
  SpawnTrustDialogError,
} from "../agents.js";
import { CarrierStopFailedError, CarrierStopRefusedError } from "../carrier-stop.js";
import {
  type Db,
  defaultDbPath,
  openDb,
  SchemaTooNewError,
  SchemaTooOldError,
  SqliteTooOldError,
  WorkstreamNotFoundError,
} from "../db.js";
import {
  DelegateCallConflictError,
  DelegateNotFoundError,
  DelegateStateError,
} from "../delegates.js";
import { DriftDetectedError } from "../drift.js";
import { editDistance } from "../edit-distance.js";
import { checkDbInsideSyncDir, DbInsideSyncDirError, HazardFailError } from "../fleet-hazards.js";
import {
  HostChannelBusyError,
  HostCheckFailedError,
  HostExistsError,
  HostHandbackError,
  HostInUseError,
  HostNotFoundError,
  HostProfileError,
  HostSpawnUnsupportedError,
} from "../hosts.js";
import { HumanNeededInvalidError } from "../human-needed.js";
import { GroupIdAmbiguousError, GroupNotFoundError, LogKindReservedError } from "../logs.js";
import {
  HerdrCommandOverrideError,
  HerdrUnsupportedCliError,
  HerdrWorkspaceGroupCloseError,
  MuxError,
  NoMultiplexerError,
  PaneNotFoundError,
} from "../mux.js";
import { NextArgError } from "../next.js";
import { capPlan, computeNext, NEXT_BYTES, type NextOutcome } from "../next-hints.js";
import {
  isAgentListArgv,
  isJsonMode,
  isStateVerbArgv,
  pc,
  printUsageHuman,
  usageLines,
} from "../output.js";
import { encodeEnvelope, type NextPlan, RESULT_BYTES } from "../output-contract.js";
import { RebuildTargetExistsError, RebuildTargetIsSourceError } from "../rebuild.js";
import { RemoteCloseRefusedError, RemotePrepareError } from "../remote-agent.js";
import { RemoteCollectError, RemoteCollectUnsupportedError } from "../remote-collect.js";
import { RemoteRecoveryRefusedError } from "../remote-recovery.js";
import { RevisionMismatchError } from "../revision.js";
import { syncDir } from "../segments.js";
import {
  ambientFlush,
  ambientIngest,
  SyncPeerNotFoundError,
  SyncPeerRefAmbiguousError,
  SyncSourceNotFoundError,
  SyncSourceTooNewError,
  syncEnabled,
} from "../sync.js";
import { assertSyncDirV2, SyncDirForeignError } from "../sync-dir-guard.js";
import { TaskAutoArgError, TaskAutoRefusedError } from "../task-auto.js";
import { TaskBriefMissingError } from "../task-brief.js";
import { candidateTaskIds } from "../tasks/id.js";
import {
  ClaimerNotRegisteredError,
  CloseNeedsEvidenceError,
  CrossWorkstreamEdgeError,
  CycleError,
  HumanTaskError,
  InvalidSubstateError,
  SubstateReasonRequiredError,
  TaskAlreadyOwnedError,
  TaskExistsError,
  TaskHasOpenBlockersError,
  TaskIdInvalidError,
  TaskInTriageError,
  TaskNoteRangeError,
  TaskNotFoundError,
  TaskNotInWorkstreamError,
  TaskParkedError,
  TaskParkStateError,
  TaskTitleSlugEmptyError,
} from "../tasks.js";
import { currentCallers } from "../wait-callers.js";
import { WaitResumeNotFoundError } from "../wait-resume.js";
import { WaveNotFoundError } from "../wait-types.js";
import {
  WorkstreamExistsError,
  WorkstreamHasAgentsError,
  WorkstreamNameInvalidError,
  WorkstreamNameReservedError,
  WorkstreamPanesLiveError,
  WorkstreamRevisionMismatchError,
  WorkstreamSessionLiveError,
} from "../workstream.js";
import {
  WorktreeBranchExistsError,
  WorktreeDirtyError,
  WorktreeHeldError,
  WorktreePathNotEmptyError,
  WorktreeVcsRequiredError,
} from "../worktree.js";

export class UsageError extends Error {
  override readonly name: string = "UsageError";
  /** `false`: the message already names the fix, so emitError prints it
   *  alone (exit 2) instead of following it with the verb's --help. */
  readonly showHelp: boolean;
  constructor(message: string, opts: { showHelp?: boolean } = {}) {
    super(message);
    this.showHelp = opts.showHelp ?? true;
  }
}

/** `mu db backup` was pointed at a file that exists: backup never overwrites. A collision, exit 4,
 *  like `mu rebuild`'s (RebuildTargetExistsError). */
export class BackupTargetExistsError extends Error {
  override readonly name = "BackupTargetExistsError";
  constructor(public readonly path: string) {
    super(`${path} already exists: mu db backup never overwrites`);
  }
}

/** `mu db query` hit `no such column`: the message already names the columns
 *  and the closest ones, so it is one line (exit 2, no help dump). */
export class SqlColumnError extends UsageError {
  override readonly name = "SqlColumnError";
  constructor(message: string) {
    super(message, { showHelp: false });
  }
}

/**
 * Internal CLI-control sentinel for handlers that already rendered their
 * success/partial-success payload and only need `handle()` to exit with a
 * non-zero code AFTER its DB-close finally has run. Unlike `UsageError` it
 * emits no stderr text; it is a return-code mechanism owned by handle().
 */
export class CliExitError extends Error {
  override readonly name = "CliExitError";
  constructor(
    public readonly exitCode: number,
    /** With `--json`: the ONE failure document (instead of a success one). */
    public readonly failure?: JsonFailure,
  ) {
    super(`exit ${exitCode}`);
  }
}

/** A failure document a verb builds itself: the error class and message, the
 *  partial evidence it already produced (`data`), and receipts (`extra`). */
export interface JsonFailure {
  error: string;
  message: string;
  data?: unknown;
  extra?: Record<string, unknown>;
}

/** The sentinel for a verb that fails AFTER producing its report (doctor's
 *  drift/hazard/no-mux): `--json` prints one failure document holding `data`,
 *  with the real error's class, message and exit code. */
export function failureWithData(err: unknown, data: unknown): CliExitError {
  return new CliExitError(classifyError(err).exitCode, {
    error: err instanceof Error ? err.name : "Error",
    message: err instanceof Error ? err.message : String(err),
    data,
  });
}

// ─── The ONE finalisation of `next` ──────────────────────────────────
//
// Every result's `next` (success JSON, failure JSON, partial failure, human
// text) is `finalNext()`: `computeNext` (src/next-hints.ts) over the DB
// `handle()` holds open, read when the result is printed, so after the verb's
// writes. A verb only DECLARES what it did (`setNextOutcome`) and its scope
// (`setNextScope`, done by resolveWorkstream); it never builds a hint. Outside
// `handle()` (parse errors, no DB) the plan is null.

let nextDb: Db | undefined;
let nextScope: string | undefined;
let nextOutcome: NextOutcome | undefined;
let nextArmed = false;

/** What the verb did that a follow-up depends on (a delivered send, a preview, …). */
export function setNextOutcome(outcome: NextOutcome): void {
  nextOutcome = outcome;
}

/** The verb registered owed work (`armWait`): the bare wait may follow it. */
export function setNextArmed(): void {
  nextArmed = true;
}

/** The resolved workstream of the call (resolveWorkstream records it). */
export function setNextScope(workstream: string): void {
  nextScope = workstream;
}

/** The plan of this result, or null outside handle(). Best effort: a hint never fails a verb. */
export function finalNext(): NextPlan {
  if (nextDb === undefined) return null;
  try {
    return computeNext(nextDb, {
      callers: currentCallers(),
      armed: nextArmed,
      ...(nextScope !== undefined ? { workstream: nextScope } : {}),
      ...(nextOutcome !== undefined ? { outcome: nextOutcome } : {}),
    });
  } catch {
    return null;
  }
}

/**
 * The plan cut to what is left of the result budget once `base` (the document
 * with `next:null`) is counted: a seq is dropped whole, an alt from its tail.
 * A document already over budget (a brief is never cut) keeps NEXT_BYTES.
 */
export function fitNext(plan: NextPlan, baseBytes: number): NextPlan {
  const left = baseBytes > RESULT_BYTES ? NEXT_BYTES : RESULT_BYTES - baseBytes + 4;
  return capPlan(plan, Math.min(NEXT_BYTES, left)).next;
}

/** The human rendering of a plan: one `next:` line per command. */
export function printNext(plan: NextPlan, sink: "stdout" | "stderr"): void {
  if (plan === null) return;
  const out = sink === "stderr" ? console.error : console.log;
  const head = plan.mode === "seq" ? "next (in order):" : "next:";
  if (plan.commands.length === 1) out(pc.dim(`${head} ${plan.commands[0]}`));
  else {
    out(pc.dim(head));
    for (const c of plan.commands) out(pc.dim(`  ${c}`));
  }
}

/** One failure document on STDOUT, its plan fitted to the budget. */
function printFailure(env: {
  error: string;
  message: string;
  exit: number;
  extra?: Record<string, unknown>;
  data?: unknown;
}): void {
  const doc = (next: NextPlan) => encodeEnvelope({ ok: false, ...env, next });
  console.log(doc(fitNext(finalNext(), Buffer.byteLength(doc(null)))));
}

// ─── Active-command tracking for the validation-error contract ──────
//
// `setActiveCommand` is called by `handle()` at the entry of every
// .action() body. `emitError()` reads it to render the failing
// subcommand's --help on usage errors. We thread it via module-local
// state (mirror of `isJsonMode` reading process.argv) instead of
// constructor-arg-ing every UsageError throw site — there are many
// of them, several inside helpers (resolveWorkstream, parseStatusFilter)
// that don't know which subcommand called them.
//
// Cleared in handle()'s finally so a sequence of test calls (which
// re-use the process) doesn't bleed state across cases.

let activeCommand: Command | undefined;

/** Map a commander.CommanderError to (exitCode, label). The codes that
 *  represent successful early-exits (--help, --version) are mapped to
 *  exitCode 0 and a sentinel label so emitError can short-circuit
 *  without printing anything. All other codes are uniform exit 2 —
 *  the documented "usage error" lane that handler-thrown UsageError
 *  also occupies. */
function classifyCommanderError(err: CommanderError): { label: string; exitCode: number } {
  if (
    err.code === "commander.helpDisplayed" ||
    err.code === "commander.help" ||
    err.code === "commander.version"
  ) {
    return { label: "help", exitCode: 0 };
  }
  // Everything else is a usage mistake (missing required option,
  // unknown option, unknown command, missing argument, type-coercion
  // via InvalidArgumentError). Renumber commander's default exit 1
  // to mu's documented exit 2 so the surface is uniform with the
  // handler-thrown UsageError lane.
  return { label: "error", exitCode: 2 };
}

/** Predicate: is this error a usage-class one for which we should
 *  render the failing subcommand's --help? Three families:
 *    1. CommanderError (missing required option, unknown option,
 *       unknown command, missing argument, type-coercion failure).
 *    2. UsageError (handler-thrown mutex / arity / range checks).
 *    3. Typed *Invalid* domain errors that fault on a value the
 *       operator typed at the CLI (workstream name, task id, prune-flag
 *       combination). The verb's --help would
 *       have explained the constraint; show it.
 *
 */
function isUsageClassError(err: unknown): boolean {
  if (err instanceof CommanderError) return true;
  if (err instanceof UsageError) return err.showHelp;
  if (
    // The verb's --help would explain a constraint on a value the operator typed. (A workstream
    // name, an empty slug: their messages name the fix, so they are one line, no help.)
    err instanceof WorkstreamNameReservedError ||
    err instanceof TaskIdInvalidError
  ) {
    return true;
  }
  return false;
}

/**
 * Raised when a bare entity name is used at the CLI but the resolution
 * context (no --workstream and no injected worker scope) is empty
 * AND the same name lives in two or more workstreams. Exit 4 (conflict)
 * via classifyError. The message names every candidate workstream.
 *
 * See verb_arg_qualified_workstream_name for the design.
 */
export class NameAmbiguousError extends Error {
  override readonly name = "NameAmbiguousError";
  constructor(
    public readonly entityName: string,
    public readonly candidates: readonly string[],
    public readonly kind: "task" | "agent",
  ) {
    super(
      `${kind} name "${entityName}" exists in ${candidates.length} workstreams (${candidates.join(", ")}); pass -w <workstream> or use the qualified form <workstream>/${entityName}`,
    );
  }
}

/**
 * Map a typed error to (label, exitCode). The label is the prefix
 * before the message in human output (e.g. "conflict", "not found");
 * the exit code is what the process exits with.
 *
 * Order matters: more-specific classes first. The fallthrough at the
 * end is the generic exit-1 catch-all.
 */
export function classifyError(err: unknown): { label: string; exitCode: number } {
  if (
    err instanceof UsageError ||
    err instanceof TaskAutoArgError ||
    err instanceof NextArgError ||
    // `mu log --kind task|note|...`: a tag naming a projectable entity.
    err instanceof LogKindReservedError ||
    // abort on a non-pi agent: there is no control socket to carry it.
    err instanceof AgentAbortNeedsCtlError ||
    // send --fresh on an agent with no control socket: wrong recipe.
    err instanceof AgentFreshNeedsCtlError ||
    // an empty send or unsupported slash command to a pi agent: ctl cannot carry it.
    err instanceof AgentEmptySendError ||
    err instanceof AgentSlashCommandUnsupportedError ||
    err instanceof WorkstreamNameInvalidError ||
    err instanceof WorkstreamNameReservedError ||
    // A host profile field or template mu refuses: nothing is stored or run.
    err instanceof HostProfileError ||
    // collect on an agent with no remote worktree of its own.
    err instanceof RemoteCollectUnsupportedError ||
    // The active mux cannot run a profile's shell template: refused before any effect.
    err instanceof HostSpawnUnsupportedError ||
    // Same typed-*Invalid* class: a task id or a title the operator
    // typed that cannot become an id. isUsageClassError prints --help.
    err instanceof TaskIdInvalidError ||
    err instanceof TaskTitleSlugEmptyError ||
    // A close without its reason: a missing flag (--evidence, --why), whether the CLI, the SDK
    // or a `mu sql --write` guard found it. One lane (usage, 2), no help dump: the message
    // names the command.
    err instanceof CloseNeedsEvidenceError ||
    err instanceof SubstateReasonRequiredError ||
    // A blank reason or missing caller identity for `mu human-needed`.
    err instanceof HumanNeededInvalidError ||
    // The operator asked the ACTIVE backend for something it cannot do:
    // a `--cli` the mux does not recognise as an agent kind, or a
    // `--command` / `MU_<CLI>_COMMAND` override it cannot honour. The
    // substrate is healthy and answered precisely, so this is the usage
    // lane, not the exit-5 "mux is down" lane. Both refuse BEFORE any
    // side effect.
    err instanceof HerdrUnsupportedCliError ||
    err instanceof HerdrCommandOverrideError ||
    // herdr refused to close a workspace with linked worktree
    // workspaces. Same lane: the substrate answered precisely and only
    // the operator can decide whether those siblings may die.
    err instanceof HerdrWorkspaceGroupCloseError
  ) {
    return { label: "error", exitCode: 2 };
  }
  if (
    err instanceof AgentNotFoundError ||
    err instanceof DelegateNotFoundError ||
    err instanceof WaitResumeNotFoundError ||
    err instanceof WaveNotFoundError ||
    err instanceof TaskNotFoundError ||
    err instanceof TaskNoteRangeError ||
    err instanceof HostNotFoundError ||
    err instanceof WorkstreamNotFoundError
  ) {
    // WorkstreamNotFoundError originates inside resolveWorkstreamId
    // (src/db.ts) — it's the canonical resolve-time miss for the
    // first leg of the SDK boundary (operator-name → surrogate id).
    // Without this entry it fell through to generic exit 1, robbing
    // operators of the same -> exit-3 mapping that AgentNotFoundError /
    // TaskNotFoundError get. (schema_v5_cli_boundary)
    return { label: "not found", exitCode: 3 };
  }
  if (
    err instanceof NameAmbiguousError ||
    // A host profile that exists, is at its channel cap, or still holds channels.
    err instanceof HostExistsError ||
    err instanceof HostChannelBusyError ||
    // A remote worktree close refuses to remove (dirty, uncollected HEAD, moved).
    err instanceof RemoteCloseRefusedError ||
    // A recovery record that is ambiguous, claimed, changed or pinned to another profile.
    err instanceof RemoteRecoveryRefusedError ||
    // A recorded job handle whose stop template changed, or whose profile is gone: nothing ran.
    err instanceof CarrierStopRefusedError ||
    // A respawn of a name whose older incarnation's job was never stopped.
    err instanceof CarrierJobUnstoppedError ||
    // A lease close with no handle it can stop, or a reservation it had to keep.
    err instanceof CarrierJobUnresolvedError ||
    err instanceof CarrierJobKeptError ||
    err instanceof HostInUseError ||
    err instanceof AgentExistsError ||
    err instanceof AgentNameExhaustedError ||
    err instanceof TaskExistsError ||
    err instanceof BackupTargetExistsError ||
    err instanceof TaskAutoRefusedError ||
    err instanceof TaskBriefMissingError ||
    err instanceof DelegateCallConflictError ||
    err instanceof DelegateStateError ||
    err instanceof TaskAlreadyOwnedError ||
    err instanceof TaskHasOpenBlockersError ||
    // Substate refusals (ts_4): a park from the wrong phase, a claim on a
    // parked task, a pair outside TASK_SUBSTATES.
    err instanceof TaskParkStateError ||
    err instanceof TaskParkedError ||
    err instanceof HumanTaskError ||
    err instanceof TaskInTriageError ||
    err instanceof InvalidSubstateError ||
    err instanceof TaskNotInWorkstreamError ||
    err instanceof AgentNotInWorkstreamError ||
    err instanceof AgentSendUnsupportedError ||
    err instanceof CycleError ||
    err instanceof CrossWorkstreamEdgeError ||
    err instanceof WorktreeDirtyError ||
    err instanceof WorktreeBranchExistsError ||
    err instanceof WorktreeHeldError ||
    err instanceof WorktreePathNotEmptyError ||
    err instanceof WorktreeVcsRequiredError ||
    err instanceof ClaimerNotRegisteredError ||
    err instanceof SchemaTooOldError ||
    err instanceof SchemaTooNewError ||
    err instanceof SqliteTooOldError ||
    err instanceof SyncSourceTooNewError ||
    err instanceof DbInsideSyncDirError ||
    err instanceof SyncDirForeignError ||
    err instanceof WorkstreamExistsError ||
    err instanceof WorkstreamRevisionMismatchError ||
    err instanceof RevisionMismatchError ||
    err instanceof WorkstreamHasAgentsError ||
    err instanceof WorkstreamPanesLiveError ||
    err instanceof WorkstreamSessionLiveError ||
    // Rebuild refuses to overwrite an existing target or to write onto
    // the source DB: both are name/state collisions, not usage errors.
    err instanceof RebuildTargetExistsError ||
    err instanceof RebuildTargetIsSourceError ||
    // An abbreviated group id matching several groups: the operator must
    // disambiguate. Guessing could pick the wrong group.
    err instanceof GroupIdAmbiguousError
  ) {
    return { label: "conflict", exitCode: 4 };
  }
  if (
    err instanceof GroupNotFoundError ||
    // A `--repair <peer>` ref or a `--from <path>` that names nothing:
    // the same resolve-time-miss lane as every other not-found.
    err instanceof SyncPeerNotFoundError ||
    err instanceof SyncSourceNotFoundError
  ) {
    return { label: "not found", exitCode: 3 };
  }
  if (err instanceof SyncPeerRefAmbiguousError) {
    // An abbreviated peer id matching several peers. Same rule as an
    // ambiguous group id: the operator disambiguates, mu never guesses.
    return { label: "conflict", exitCode: 4 };
  }
  if (err instanceof DriftDetectedError) {
    // 5 = substrate unavailable/inconsistent. Drift means the SQLite
    // substrate is internally inconsistent (the log and the tables
    // disagree), which is exactly that category — not a usage mistake (2)
    // and not a name collision (4).
    return { label: "drift", exitCode: 5 };
  }
  if (err instanceof HazardFailError) {
    // Same lane as drift: a doctor `fail` row (the DB inside
    // MU_SYNC_DIR) means the substrate is unsafe to keep writing.
    return { label: "doctor", exitCode: 5 };
  }
  if (isSqliteBusy(err)) {
    // Another process held the write lock past busy_timeout (5s, set in
    // openDb). The DB is unavailable, not wrong: retry later.
    return { label: "db locked", exitCode: 5 };
  }
  if (err instanceof AgentSpawnCliNotFoundError) {
    // Pre-flight failure: --cli's resolved binary isn't on PATH. We
    // refused before any side effect, so this is the cleanest
    // operator-typo lane there is. Generic exit 1 (substrate-class)
    // matches AgentDiedOnSpawnError / AgentSpawnStartupError below —
    // all three are "the spawn can't produce a working agent".
    return { label: "spawn cli not found", exitCode: 1 };
  }
  if (err instanceof AgentSendIndeterminateError) {
    // Delivery unknown: same lane as ctl unreachable, distinct label.
    return { label: "send delivery unknown", exitCode: 1 };
  }
  if (err instanceof AgentCtlUnreachableError) {
    // The agent runs, but mu cannot control it: same "spawn can't
    // produce a working agent" lane as the spawn failures around it.
    return { label: "ctl unreachable", exitCode: 1 };
  }
  if (err instanceof AgentDiedOnSpawnError || err instanceof SpawnTrustDialogError) {
    // Substrate-level failure (CLI exited at spawn). The message is
    // already rich (includes captured scrollback). Generic exit 1.
    return { label: "spawn failed", exitCode: 1 };
  }
  if (err instanceof AgentSpawnStartupError) {
    // Pane is alive but the spawned CLI parked at a known
    // provider-auth-failure prompt. Same generic exit 1 lane as
    // AgentDiedOnSpawnError — both are "the spawn looked OK to mu but
    // the operator can't actually use this agent". The error message
    // already carries the matched line + scrollback.
    return { label: "spawn startup error", exitCode: 1 };
  }
  if (err instanceof AgentExtensionOutdatedError) {
    // The agent runs, but its extension cannot serve this op yet:
    // a state conflict the operator resolves by /reload or respawn.
    return { label: "extension outdated", exitCode: 4 };
  }
  if (
    err instanceof AgentReplacedError ||
    err instanceof AgentLockBusyError ||
    err instanceof SpawnAttemptBusyError ||
    err instanceof SpawnAttemptEndedError ||
    err instanceof SpawnAttemptFailedError ||
    err instanceof SpawnAttemptGuardError ||
    err instanceof SpawnTaskStateError ||
    err instanceof SpawnReceiptConflictError
  ) {
    // The name changed hands, or another mu holds the agent: retry after a preview.
    return { label: "conflict", exitCode: 4 };
  }
  if (err instanceof AgentPaneStillAliveError) {
    // The pane outlived the kill: a state conflict the operator resolves.
    return { label: "conflict", exitCode: 4 };
  }
  if (err instanceof AgentRestartedError) {
    // The run being waited on died with the old pi process: state conflict.
    return { label: "conflict", exitCode: 4 };
  }
  if (err instanceof AgentBusyError) {
    // The agent's state collides with the request; abort or --force.
    return { label: "conflict", exitCode: 4 };
  }
  if (err instanceof AgentAbortTimeoutError) {
    // Still busy after --timeout: substrate lane, try again.
    return { label: "timeout", exitCode: 5 };
  }
  if (
    err instanceof RemotePrepareError ||
    err instanceof RemoteCollectError ||
    // The stop template ran and did not prove the job stopped: the host's answer, substrate.
    err instanceof CarrierStopFailedError
  ) {
    // The host's own exec step failed or answered nothing usable: substrate, not usage.
    return { label: "remote host", exitCode: 5 };
  }
  if (err instanceof HostHandbackError) {
    // A host template asked for a human (exit 3): never retried, never auto-recorded.
    return { label: "human needed", exitCode: 5 };
  }
  if (err instanceof HostCheckFailedError) {
    // The host (or its forward) failed a check step: substrate, not usage.
    return { label: "host check", exitCode: 5 };
  }
  if (err instanceof NoMultiplexerError) {
    return { label: "no multiplexer", exitCode: 5 };
  }
  if (err instanceof MuxError || err instanceof PaneNotFoundError) {
    // MuxError is the base of the per-backend families (TmuxError, …),
    // so one instanceof covers every multiplexer.
    return { label: "mux", exitCode: 5 };
  }
  return { label: "error", exitCode: 1 };
}

/** better-sqlite3's SqliteError for a lock it could not get: SQLITE_BUSY
 *  ('database is locked') and SQLITE_LOCKED, with their extended codes. */
function isSqliteBusy(err: unknown): boolean {
  if (!(err instanceof Error) || err.name !== "SqliteError") return false;
  const code = (err as { code?: unknown }).code;
  return (
    typeof code === "string" && (code.startsWith("SQLITE_BUSY") || code.startsWith("SQLITE_LOCKED"))
  );
}

/** prose-quoting: on the verbs that carry prose, "too many arguments"
 *  nearly always means an apostrophe ended a single-quoted string and
 *  bash split the rest into words. The message says so and names stdin. */
function quotingHint(err: unknown, cmd: Command | undefined): string | undefined {
  if (!(err instanceof CommanderError) || err.code !== "commander.excessArguments") return;
  const verb = `${cmd?.parent?.name()} ${cmd?.name()}`;
  if (verb !== "task add" && verb !== "task note" && verb !== "agent send") return;
  return "; a quote in the text probably ended the string early (an apostrophe inside '...'): pass the text on stdin as - with a quoted heredoc (<<'EOF')";
}

/**
 * An unknown verb, as one line: `unknown command 'x'` plus the closest verb
 * of `cmd` (or `mu --help`). The root takes no arguments, so commander calls
 * an unknown top-level verb "too many arguments"; a namespace says
 * "unknown command". undefined for any other error.
 */
function unknownVerb(err: unknown, cmd: Command | undefined): string | undefined {
  if (!(err instanceof CommanderError) || cmd === undefined) return;
  const root = cmd.parent === null;
  // An unknown option on the bare root: the 70-line root help is no answer, `mu --help` is.
  if (root && err.code === "commander.unknownOption") {
    const opt = /'([^']*)'/.exec(err.message)?.[1];
    if (opt !== undefined) return `unknown option '${opt}'; see mu --help`;
  }
  let word: string | undefined;
  if (err.code === "commander.unknownCommand") word = /'([^']*)'/.exec(err.message)?.[1];
  else if (err.code === "commander.excessArguments" && root) word = cmd.args[0];
  if (word === undefined) return;
  // The verbs one level down count too (`mu next` → `mu task next`); a direct one wins a tie.
  const path = root ? "mu" : `mu ${cmd.name()}`;
  const names = cmd.commands.flatMap((c) => [c.name(), ...c.aliases()].map((n) => [n, n]));
  // `task` verbs first: they win a tie (`mu show` → `mu task show`, not `mu agent show`).
  const nested = [...cmd.commands]
    .sort((a, b) => Number(b.name() === "task") - Number(a.name() === "task"))
    .flatMap((c) => c.commands.map((g) => [g.name(), `${c.name()} ${g.name()}`]));
  // The shell's spellings of a verb: `ls` is `list`, `rm` is `delete`.
  const alias = { ls: "list", rm: "delete" }[word as "ls" | "rm"];
  if (alias !== undefined && cmd.commands.some((c) => c.name() === alias)) {
    const p = root ? "mu" : `mu ${cmd.name()}`;
    return `unknown command '${word}' (closest: ${p} ${alias}); see ${p} --help`;
  }
  // A plural noun names its namespace's list: `mu tasks` → `mu task list`.
  const singular = word.endsWith("s") ? word.slice(0, -1) : undefined;
  const ns = cmd.commands.find((c) => c.name() === singular);
  if (ns?.commands.some((g) => g.name() === "list")) {
    return `unknown command '${word}' (closest: ${path} ${ns.name()} list); see ${path} --help`;
  }
  let best: string | undefined;
  let bestD = Number.POSITIVE_INFINITY;
  for (const [n, full] of [...names, ...nested] as [string, string][]) {
    const d = n === word ? 0 : n.startsWith(word) || word.startsWith(n) ? 1 : editDistance(n, word);
    if (d < bestD) [best, bestD] = [full, d];
  }
  const near =
    best !== undefined && bestD <= Math.max(word.length <= 3 ? 1 : 2, Math.floor(word.length / 3));
  return `unknown command '${word}'${near ? ` (closest: ${path} ${best})` : ""}; see ${path} --help`;
}

/** Render the error and return the resolved exit code. Returning the
 *  exitCode lets `handle` reuse it instead of re-classifying the same
 *  error twice.
 *
 *  Usage-class errors (commander +
 *  UsageError + typed *Invalid*) ALSO emit the failing subcommand's
 *  --help (human path) or a structured `usage` field (JSON path); an
 *  unknown verb is one line naming the closest verb instead. */
function emitError(err: unknown): number {
  if (err instanceof CliExitError) {
    const f = err.failure;
    if (f !== undefined) {
      printFailure({
        error: f.error,
        message: f.message,
        exit: err.exitCode,
        ...(f.extra !== undefined ? { extra: f.extra } : {}),
        ...(f.data !== undefined ? { data: f.data } : {}),
      });
    } else if (!isJsonMode()) printNext(finalNext(), "stderr");
    return err.exitCode;
  }

  // Commander's --help / --version success-exits arrive as CommanderError
  // with exitCode 0. helpInformation() has already been written by
  // commander itself; nothing more to do.
  if (err instanceof CommanderError) {
    const { exitCode } = classifyCommanderError(err);
    if (exitCode === 0) return 0;
  }

  const message = err instanceof Error ? err.message : String(err);
  const { label, exitCode } =
    err instanceof CommanderError ? classifyCommanderError(err) : classifyError(err);
  const errClass = err instanceof Error ? err.name : "Error";
  const unknown = unknownVerb(err, activeCommand);
  // Strip commander's own "error: " prefix — we re-add our own "error: "
  // (red, in the human path) and don't want "error: error: ...".
  const cleanMessage =
    unknown ??
    (err instanceof CommanderError
      ? // `message` is one line (the output contract): commander puts its suggestion on a second one.
        message.replace(/^error: /, "").replace(/\s*\n\s*/g, " ")
      : message) + (quotingHint(err, activeCommand) ?? "");
  const usageCmd = isUsageClassError(err) ? activeCommand : undefined;
  // A send whose delivery is unknown: its receipt check (never a resend), from the row it went to.
  if (
    err instanceof AgentSendIndeterminateError &&
    err.msgId !== undefined &&
    err.agentId !== undefined
  ) {
    setNextOutcome({
      kind: "send-unknown",
      agent: { name: err.agentName, workstream: err.workstream },
      msgId: err.msgId,
      agentId: err.agentId,
    });
  }

  // `mu state` and `mu agent list` are JSON without asking; a machine with no multiplexer is
  // still the one human line on stderr every other verb prints (the install hint), not a
  // document on stdout.
  const humanNoMux =
    err instanceof NoMultiplexerError &&
    (isStateVerbArgv(process.argv) || isAgentListArgv(process.argv)) &&
    !process.argv.some((a) => a === "--json" || a.startsWith("--json="));
  if (isJsonMode() && !humanNoMux) {
    // ONE failure document on STDOUT, the real exit code beside it: a caller
    // that pipes `2>/dev/null` loses nothing.
    const extra: Record<string, unknown> = {};
    if (usageCmd) extra.usage = usageLines(usageCmd);
    // A stale `--if` names both revisions; a wrong task id its near ids.
    if (err instanceof RevisionMismatchError) {
      extra.expected_rev = err.expectedRev;
      extra.current_rev = err.currentRev;
    }
    if (err instanceof TaskNotFoundError && err.candidateIds.length > 0) {
      extra.candidate_ids = err.candidateIds;
    }
    if (err instanceof NameAmbiguousError) extra.candidates = err.candidates;
    // A send whose delivery is unknown still names its receipt, structurally.
    if (err instanceof AgentSendIndeterminateError && err.msgId !== undefined) {
      extra.msgId = err.msgId;
      if (err.incarnation !== undefined) extra.incarnation = err.incarnation;
      extra.delivery = "unknown";
    }
    printFailure({
      error: errClass,
      message: cleanMessage,
      exit: exitCode,
      ...(Object.keys(extra).length > 0 ? { extra } : {}),
      ...(err instanceof HostHandbackError
        ? { data: { handback: true, host: err.host, template: err.field, detail: err.detail } }
        : {}),
    });
    return exitCode;
  }

  console.error(pc.red(`${label}: ${cleanMessage}`));
  if (err instanceof TaskNotFoundError && err.candidateIds.length > 0) {
    console.error(pc.dim(`similar: ${err.candidateIds.join(", ")}`));
  }
  printNext(finalNext(), "stderr");
  if (usageCmd && unknown === undefined) {
    printUsageHuman(usageCmd);
  }
  return exitCode;
}

export interface HandleOptions {
  /**
   * Opt OUT of the ambient sync hook. Exactly one verb sets this: `mu
   * sync`, which IS the pass (flush, then ingest, so its report counts
   * what it did rather than "ingested 0" after the hook ate everything).
   * Every other verb, `mu sql` included, ingests before and flushes
   * after. The DB-inside-MU_SYNC_DIR refusal does not honour this flag.
   */
  ambientSync?: false;
}

/** Refuse to open a DB that lives inside MU_SYNC_DIR. Runs before
 *  `openDb` for every verb, ambient or not, so nothing is created or
 *  written and `mu sync` cannot bypass it. Path-based, symlinks resolved:
 *  it fires even when the DB file does not exist yet. `mu pi migrate` (which
 *  opens no DB, so it is outside handle()) calls it too. */
export function refuseDbInsideSyncDir(): void {
  const dir = syncDir();
  if (dir === null) return;
  const dbPath = defaultDbPath();
  if (checkDbInsideSyncDir(dbPath, dir).severity === "fail") {
    throw new DbInsideSyncDirError(dbPath, dir);
  }
  // A 3.x peer shares the folder: mu 4 neither writes into it nor reads it.
  assertSyncDirV2(dir);
}

/** Wrap an async handler so typed errors become specific exit codes.
 *
 *  The optional `command` arg is the failing subcommand's `Command`
 *  (commander's `this` in a `.action(function () { ... })` body).
 *  When supplied, usage-class errors thrown inside `fn` will render
 *  that subcommand's --help (human) or `usage` JSON (--json).
 *
 *  THE AMBIENT-SYNC SEAM. `handle()` is the one place every verb passes
 *  through, and it is ALREADY async while most verb bodies are
 *  synchronous — which is exactly why the hook belongs here and nowhere
 *  else. `ambientFlush` is async (it takes a cross-process file lock); if the
 *  hook lived inside the verbs, ~63 synchronous handlers would have had
 *  to become async to await it. Here, one `await` before `fn(db)` and one
 *  after covers all of them, and no verb learns that sync exists.
 *
 *  Order is load-bearing: INGEST before the body (so the verb reads the
 *  freshest state transport has delivered) and FLUSH after (so the ops
 *  the verb just wrote are in this machine's segment before the process
 *  exits, rather than waiting for the next invocation).
 *
 *  Neither half can fail the command — see src/sync.ts. */
export function handle(
  fn: (db: Db) => Promise<void>,
  command?: Command,
  opts?: HandleOptions,
): () => Promise<void> {
  return async () => {
    let db: Db | undefined;
    let exitCode: number | undefined;
    activeCommand = command;
    // The single `if` the unconfigured single-machine case pays: one env
    // var read, no promise allocated, no filesystem touched.
    const ambient = opts?.ambientSync !== false && syncEnabled();
    try {
      refuseDbInsideSyncDir();
      db = openDb();
      if (ambient) await ambientIngest(db);
      nextDb = db;
      await fn(db);
      if (!isJsonMode()) printNext(finalNext(), "stdout");
    } catch (err) {
      // Read-only suggestions for a wrong task id, from the partition the
      // exact lookup missed in. Best effort: a hint never masks the miss.
      if (err instanceof TaskNotFoundError && err.workstream !== undefined && db !== undefined) {
        try {
          err.candidateIds = candidateTaskIds(db, err.workstream, err.lookupId);
        } catch {}
      }
      exitCode = emitError(err);
    } finally {
      activeCommand = undefined;
      nextDb = undefined;
      nextScope = undefined;
      nextOutcome = undefined;
      nextArmed = false;
      // Flush even on the error path: the verb may have committed ops
      // before throwing (a cascade that hit a cycle halfway), and those
      // ops are already canonical in `ops`. Withholding them from the
      // segment would make this machine's history depend on whether the
      // command happened to exit 0.
      if (ambient && db !== undefined) {
        try {
          await ambientFlush(db);
        } catch {
          // ambientFlush is already total; belt and braces.
        }
      }
      try {
        db?.close();
      } catch {
        // best effort
      }
    }
    if (exitCode !== undefined) {
      // A document piped to a slow reader is still in the stream's buffer: drain it,
      // or process.exit truncates the one result a caller is waiting for.
      if (process.stdout.writableLength > 0) {
        await new Promise<void>((resolve) => process.stdout.write("", () => resolve()));
      }
      process.exit(exitCode);
    }
  };
}

/** Translate a commander parse-time error into mu's wire format,
 *  then return the exit code. Used by the top-level `parseAsync` catch
 *  in cli.ts so commander mistakes go through the same emitError
 *  pipeline that handler-thrown errors use. */
export function emitParseError(err: unknown, failingCommand: Command | undefined): number {
  activeCommand = failingCommand;
  try {
    return emitError(err);
  } finally {
    activeCommand = undefined;
  }
}

/** Walk argv tokens against the program tree to find the deepest
 *  matching subcommand. Used by parseAsync's catch to identify which
 *  subcommand commander was processing when it threw. Skips options the
 *  current command declares (and their values) the way commander's
 *  parseOptions does, so a leading global (`mu --json task list`,
 *  `mu -w x --json task note …`) still resolves the verb. Stops at an
 *  unknown option or token. */
export function findCommandForArgv(root: Command, argv: readonly string[]): Command {
  let cur: Command = root;
  for (let i = 0; i < argv.length; i++) {
    const t = argv[i] ?? "";
    if (t === "--") break;
    if (t.length > 1 && t.startsWith("-")) {
      // Inline value: `--flag=value`, or `-wvalue` for a short flag.
      const long = t.startsWith("--");
      const eq = long ? t.indexOf("=") : -1;
      const flag = long ? (eq === -1 ? t : t.slice(0, eq)) : t.slice(0, 2);
      const inline = long ? eq !== -1 : t.length > 2;
      const opt = cur.options.find((o) => o.short === flag || o.long === flag);
      if (!opt || (inline && !opt.required)) break;
      if (inline || !opt.required) continue;
      i++;
      // A variadic option keeps consuming non-option tokens, as in
      // commander: `--substate a b` binds [a, b] to --substate.
      if (opt.variadic) {
        while (i + 1 < argv.length && !(argv[i + 1] ?? "-").startsWith("-")) i++;
      }
      continue;
    }
    const next: Command | undefined = cur.commands.find(
      (c) => c.name() === t || (c.aliases().includes(t) ?? false),
    );
    if (!next) break;
    cur = next;
  }
  return cur;
}
